import { useState } from 'react';
import {
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import { standardRequest } from 'shared/utils/request';
import {
  useWatch,
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  type BaseCandidate,
  type BaseResume,
  type MatchStrength,
  type ResumeParsingStatus,
  type Row,
  type Scorecard,
} from 'talent_matching/types';
import type { ResumeEmployment } from 'talent_matching/types';

export type MyGreenhouseCandidate = BaseCandidate & {
  matchStrength: MatchStrength | null;
  matchScore?: number | null;
  matchedSkills?: Array<{
    name: string;
    importance: number;
    base_skill?: string;
    exact?: boolean;
  }>;
  matchedSkillsCount: number;
  matchedExperiences?: Array<{ title: string; years_of_experience: number }>;
  matchedIndustries?: Array<{ industry: string; employer: string }>;
  yearsOfExperience?: number;
  overrideMatchStrength?: MatchStrength | null;
  scorecards?: Scorecard[];
  workPreferences: {
    employmentTypes: string[];
    worksites: string[];
    willingToRelocate: boolean;
  } | null;
  resume?:
    | (BaseResume & {
        details: {
          address: string | null;
          employments: ResumeEmployment[];
        };
        parsing_status?: ResumeParsingStatus;
      })
    | null;
};

const useQueryParams = () => {
  const { control } = useFormContext();
  const formValues = useWatch({ control });

  return {
    page: formValues.page as number,
    per_page: formValues.perPage as number,
    saved_for_later: formValues.savedForLater,
    scorecard_status: formValues.feedbackStatus,
    can_have: formValues.canHave,
    must_have: formValues.mustHave,
    does_not_have: formValues.doesNotHave,
    years_of_experience_min: formValues.minExperience,
    years_of_experience_max: formValues.maxExperience,
    company_name_filters: formValues.companyNameFilters,
    education_filters: formValues.educationFilters,
    work_preference_languages: formValues.workPreferenceLanguages,
    work_preference_employment_types: formValues.workPreferenceEmploymentTypes,
    work_preference_worksites: formValues.workPreferenceWorksites,
    work_preference_locations: formValues.workPreferenceLocations,
    work_preference_willing_to_relocate:
      formValues.workPreferenceWillingToRelocate,
  };
};

export const useMyGreenhouseCandidates = (
  path: string
): UseQueryResult<{
  rows: Row[];
  totalRows: number;
}> => {
  const queryClient = useQueryClient();
  const params = useQueryParams();
  const { page, ...filterParams } = params;

  const [totalRows, setTotalRows] = useState(0);

  const queryResult = useQuery({
    queryKey: ['my-greenhouse-candidates', filterParams, page],
    queryFn: async () => {
      const { data } = await standardRequest.get(path, {
        params: { ...filterParams, page },
      });

      setTotalRows(data.total);

      const rows: Row[] = data.candidates.map(
        (candidate: MyGreenhouseCandidate) => {
          queryClient.setQueryData(
            ['my-greenhouse-candidate', candidate.id],
            candidate
          );
          return { id: candidate.id };
        }
      );

      return {
        rows,
        totalRows: data.total,
      };
    },
    placeholderData: {
      rows: [],
      totalRows,
    },
    staleTime: 1000 * 60 * 10,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return queryResult;
};

export const useMyGreenhouseCandidate = (
  candidateId: number
): { candidate: MyGreenhouseCandidate } => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['my-greenhouse-candidate', candidateId],
    staleTime: Infinity,
    queryFn: () =>
      queryClient.getQueryData([
        'my-greenhouse-candidate',
        candidateId,
      ]) as MyGreenhouseCandidate,
    retry: 1,
  });

  return {
    candidate: query.data as MyGreenhouseCandidate,
  };
};
